import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, X, Volume2, Brain } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const AUDIO_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/soundscapes/audio`;
const TIMERS = [5, 10, 15, 20, 0]; // 0 = endless
const RECHECK = [
  { label: 'Much calmer',    stress: 1, mood: 5, color: '#7FE0A8' },
  { label: 'A bit calmer',   stress: 2, mood: 4, color: '#5BDCF5' },
  { label: 'About the same', stress: 3, mood: 3, color: '#a1a1aa' },
];

const SoundPlayerContext = createContext(null);
export const useSoundPlayer = () => useContext(SoundPlayerContext) || {};

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * Global sound player — lives ABOVE the router so a soundscape keeps playing as
 * the user moves between worlds / the dashboard. Renders a single DOM <audio>
 * (survives screen-off), a persistent "now playing" bar, and the Mind re-check
 * modal. Components call useSoundPlayer().launch(sound, pillar).
 */
export function SoundPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const tickRef = useRef(null);
  const [active, setActive] = useState(null);   // { id, name, color }
  const [pillar, setPillar] = useState('mental');
  const [paused, setPaused] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [timerMin, setTimerMin] = useState(10);
  const [elapsed, setElapsed] = useState(0);
  const [recheckFor, setRecheckFor] = useState(null);

  // Refs mirror state for use inside long-lived interval / stop closures.
  const elapsedRef = useRef(0);
  const activeRef = useRef(null);
  const pillarRef = useRef('mental');
  const timerRef = useRef(10);
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { pillarRef.current = pillar; }, [pillar]);
  useEffect(() => { timerRef.current = timerMin; }, [timerMin]);

  const stop = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (audioRef.current) audioRef.current.pause();
    const sound = activeRef.current;
    const secs = elapsedRef.current;
    const pil = pillarRef.current;
    setActive(null); setPaused(false); setElapsed(0);
    activeRef.current = null;
    if (sound && secs >= 15) {
      api.soundscapeComplete({ id: sound.id, name: sound.name, pillar: pil, duration_seconds: secs })
        .then(() => window.dispatchEvent(new Event('wellness:refresh'))).catch(() => {});
      if (pil === 'mental') setRecheckFor(sound);
      else toast.success(`${sound.name} — Recovery session logged.`);
    }
  }, []);

  const startTimer = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    setElapsed(0); elapsedRef.current = 0;
    tickRef.current = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1; elapsedRef.current = next;
        if (timerRef.current > 0 && next >= timerRef.current * 60) setTimeout(stop, 0);
        return next;
      });
    }, 1000);
  }, [stop]);

  const setupMediaSession = (sound, pil) => {
    if (!('mediaSession' in navigator)) return;
    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({ title: sound.name, artist: 'Revivo IQ · ' + (pil === 'recovery' ? 'Recovery' : 'Mind'), album: 'Sound Shelf' });
      navigator.mediaSession.setActionHandler('pause', () => togglePause());
      navigator.mediaSession.setActionHandler('play', () => togglePause());
      navigator.mediaSession.setActionHandler('stop', () => stop());
    } catch (e) { /* noop */ }
  };

  const launch = useCallback((sound, pil = 'mental') => {
    const a = audioRef.current;
    if (!a) return;
    // Re-tapping the SAME sound while it's genuinely playing = stop. If it isn't
    // actually playing (mobile often blocks the first gesture's play()), retry instead.
    if (activeRef.current?.id === sound.id && !a.paused && a.currentTime > 0) { stop(); return; }

    // Point the single <audio> at the new source. Sound object can carry an
    // explicit `url` (used for our baked static files); else fall back to the
    // old Mongo-backed synth endpoint by id.
    const url = sound.url || `${AUDIO_BASE}/${sound.id}`;
    if (a.currentSrc !== url && a.src !== url) a.src = url;
    a.volume = volume;
    a.loop = true;
    try { a.currentTime = 0; } catch (e) { /* not seekable yet */ }

    // Reflect the selection immediately so the now-playing bar appears, but only
    // start the session timer once playback has actually begun.
    setActive(sound); setPillar(pil); setPaused(false);
    activeRef.current = sound; pillarRef.current = pil;
    setupMediaSession(sound, pil);

    const started = () => { setPaused(false); startTimer(); };
    const p = a.play();
    if (p && p.then) {
      p.then(started).catch(() => {
        // The gesture's first play() was blocked — retry once, still inside the
        // same tap's activation window, which mobile browsers do honour.
        a.play().then(started).catch(() => {
          setPaused(true);
          toast.error('Tap play to start the sound.');
        });
      });
    } else {
      started();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume, stop, startTimer]);

  const togglePause = useCallback(() => {
    const a = audioRef.current;
    if (!a || !activeRef.current) return;
    if (a.paused) { a.play().catch(() => {}); setPaused(false); }
    else { a.pause(); setPaused(true); }
  }, []);

  const changeVolume = useCallback((v) => { setVolume(v); if (audioRef.current) audioRef.current.volume = v; }, []);
  const cycleTimer = useCallback(() => {
    setTimerMin((t) => { const i = TIMERS.indexOf(t); return TIMERS[(i + 1) % TIMERS.length]; });
  }, []);

  const submitRecheck = (opt) => {
    api.submitMoodCheckin({ stress: opt.stress, mood: opt.mood })
      .then(() => { window.dispatchEvent(new Event('wellness:refresh')); toast.success('Logged — your Mind score just updated.'); })
      .catch(() => {});
    setRecheckFor(null);
  };

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  const accent = active?.color || (pillar === 'recovery' ? '#5BDCF5' : '#C08CFF');

  return (
    <SoundPlayerContext.Provider value={{ active, pillar, paused, volume, timerMin, elapsed, launch, togglePause, stop, changeVolume, cycleTimer }}>
      {children}

      {/* Persistent DOM media element — survives navigation + screen-off. */}
      <audio ref={audioRef} loop playsInline preload="auto" data-testid="sound-audio-el" />

      {/* Now-playing bar — pinned above the mobile bottom nav, follows you everywhere. */}
      {active && (
        <div className="fixed left-0 right-0 z-40 px-3 pointer-events-none lg:left-64" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}>
          <div
            className="mx-auto max-w-2xl rounded-2xl border bg-zinc-900/90 backdrop-blur-xl shadow-2xl px-3 py-2.5 flex items-center gap-3 pointer-events-auto lg:max-w-3xl"
            style={{ borderColor: `${accent}55`, boxShadow: `0 0 28px ${accent}22` }}
            data-testid="now-playing-bar"
          >
            <button onClick={togglePause} data-testid="now-playing-toggle"
              className="w-10 h-10 rounded-full grid place-items-center text-black shrink-0" style={{ background: accent }}>
              {paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-secondary font-bold text-white text-sm truncate">{active.name}</p>
              <p className="text-[11px] text-zinc-400 tabular-nums">{fmt(elapsed)}{timerMin > 0 ? ` / ${timerMin}:00` : ' · endless'}</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 w-28">
              <Volume2 className="w-4 h-4 text-zinc-500 shrink-0" />
              <input type="range" min="0" max="1" step="0.05" value={volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))} data-testid="now-playing-volume"
                className="flex-1" style={{ accentColor: accent }} />
            </div>
            <button onClick={cycleTimer} data-testid="now-playing-timer"
              className="text-[11px] font-bold px-2.5 py-1 rounded-full border shrink-0"
              style={{ borderColor: `${accent}55`, color: accent }}>
              {timerMin === 0 ? '∞' : `${timerMin}m`}
            </button>
            <button onClick={stop} data-testid="now-playing-close"
              className="w-9 h-9 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 text-zinc-300 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mind re-check — fires after a >=15s Mind sound session ends. */}
      {recheckFor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="sound-recheck">
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-zinc-900 p-6 text-center">
            <div className="w-12 h-12 rounded-full mx-auto grid place-items-center" style={{ background: '#C08CFF22', border: '1px solid #C08CFF55' }}>
              <Brain className="w-5 h-5" style={{ color: '#C08CFF' }} />
            </div>
            <h4 className="font-secondary font-bold text-xl text-white mt-3">Nice — how&apos;s your stress now?</h4>
            <p className="text-xs text-zinc-400 mt-1">One tap updates your Mind score honestly.</p>
            <div className="grid gap-2 mt-5">
              {RECHECK.map((o) => (
                <button key={o.label} onClick={() => submitRecheck(o)} data-testid={`sound-recheck-${o.stress}`}
                  className="py-2.5 rounded-xl font-secondary font-bold text-sm border transition-all hover:scale-[1.02]"
                  style={{ borderColor: `${o.color}55`, color: o.color, background: `${o.color}12` }}>
                  {o.label}
                </button>
              ))}
            </div>
            <button onClick={() => setRecheckFor(null)} data-testid="sound-recheck-skip" className="mt-3 text-xs text-zinc-500 hover:text-zinc-300">Skip</button>
          </div>
        </div>
      )}
    </SoundPlayerContext.Provider>
  );
}
